import React, { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Input, List, ListItem, Box, InputProps } from "@chakra-ui/react";

interface AutoCompleteProps extends Omit<InputProps, "onSelect" | "onSearch"> {
  suggestions: string[];
  onSelect: (value: string) => void;
  onSearch: (value: string) => void;
  value: string;
}

const AutoComplete: React.FC<AutoCompleteProps> = ({
  suggestions,
  onSelect,
  onSearch,
  value,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search calls
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      onSearch(inputValue);
    }, 100);
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [inputValue, onSearch]);

  // Sync with external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle Enter → select first suggestion if none chosen
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      e.preventDefault();
      const first = suggestions[0];
      setInputValue(first);
      onSelect(first);
      setShowSuggestions(false);
    }
  };

  return (
    <Box position="relative">
      <Input
        {...props}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setShowSuggestions(false)}
        onKeyDown={handleKeyDown}
      />

      {showSuggestions && suggestions.length > 0 && (
        <List
          position="absolute"
          width="100%"
          maxH="200px"
          overflowY="auto"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          zIndex={10}
          mt={1}
          className="no-translate"
        >
          {suggestions.map((suggestion, index) => (
            <ListItem
              key={index}
              px={3}
              py={2}
              cursor="pointer"
              onMouseDown={() => {
                setInputValue(suggestion);
                onSelect(suggestion);
                setShowSuggestions(false);
              }}
            >
              {suggestion}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default AutoComplete;
